import React from 'react';
import { GitMerge, AlertCircle, FileEdit } from 'lucide-react';
import TensionEditor from './TensionEditor';

const TensionsPanel = ({
  selectedCircle,
  tensions,
  tensionsLoading,
  tensionError,
  selectedTensionId,
  circles,
  tensionDraft,
  tensionVotes,
  tensionVotesLoading,
  onStartTension,
  onSelectTension,
  isFieldChanged,
  onUpdateField,
  onUpdateProposedField,
  onUpdateList,
  onChangeAction,
  onDelete,
  onPropose,
  onWithdraw,
  onCancel,
  onSave,
  user,
  selectedCircleIsRole = false,
}) => {
  const historyStatuses = ['applied', 'rejected', 'meeting'];
  const isHistory = (status) => historyStatuses.includes((status || '').toLowerCase());
  const activeTensions = tensions.filter((t) => !isHistory(t.status));
  const pastTensions = tensions.filter((t) => isHistory(t.status));
  const canStartTension = selectedCircle?.id && !selectedCircleIsRole;
  const tensionStatusLabel = (status) => {
    const normalized = (status || 'draft').toLowerCase();
    if (normalized === 'draft') return 'Brouillon';
    if (normalized === 'meeting') return 'En réunion';
    if (normalized === 'proposal') return 'Proposition';
    if (normalized === 'applied') return 'Appliquée';
    if (normalized.startsWith('reject')) return 'Rejetée';
    return status || 'Brouillon';
  };

  return (
  <div className="bg-white p-6 rounded-lg shadow-md h-full overflow-hidden flex flex-col">
    <div className="flex items-start justify-between mb-4">
      <div>
        <div className="flex items-center gap-2 text-sm text-purple-700 font-semibold">
          <GitMerge size={16} />
          Tensions holacratie
        </div>
        <h2 className="text-xl font-bold text-slate-900">Mes tensions</h2>
        <p className="text-slate-500 text-sm">Proposez des changements sans toucher au leader ni a la strategie.</p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onStartTension(selectedCircle)}
          className="px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center disabled:opacity-60"
          disabled={!canStartTension}
          title={
            !selectedCircle?.id
              ? 'Sélectionnez un cercle dans la structure pour lancer une tension'
              : selectedCircleIsRole
                ? 'Sélectionnez un cercle (pas un role) pour lancer une tension'
                : 'Nouveau brouillon sur ce cercle'
          }
        >
          <FileEdit size={14} className="mr-1" />
          Nouvelle tension
        </button>
      </div>
    </div>

    {tensionError && (
      <div className="mb-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">
        <AlertCircle size={16} />
        {tensionError}
      </div>
    )}

    <div className="grid grid-cols-12 gap-3 flex-1 min-h-0">
      <div className="col-span-5 bg-slate-50 border border-slate-100 rounded-lg p-3 overflow-y-auto">
        {tensionsLoading ? (
          <div className="flex items-center justify-center h-full text-slate-400 text-sm">Chargement...</div>
        ) : activeTensions.length === 0 ? (
          <div className="text-center text-slate-400 text-sm space-y-2">
            <p>Aucune tension enregistrée.</p>
            <p className="text-xs">Sélectionnez un cercle puis cliquez sur "Nouvelle tension".</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activeTensions.map((tension) => {
              const updatedAt = tension.updated_at || tension.created_at;
              return (
                <button
                  key={tension.id}
                  onClick={() => onSelectTension(tension)}
                  className={`w-full text-left p-3 rounded-lg border transition hover:border-purple-200 hover:bg-white ${
                    selectedTensionId === tension.id ? 'border-purple-400 bg-white shadow-sm' : 'border-slate-200 bg-slate-100'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-slate-900 text-sm">
                      {tension.title || tension.original_data?.name || 'Tension sans titre'}
                    </div>
                    <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-purple-100 text-purple-700 font-semibold">
                      {tensionStatusLabel(tension.status)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Cercle :{' '}
                    {tension.circle_id
                      ? circles.find((c) => c.id === tension.circle_id)?.name || '?'
                      : tension.original_data?.name || '?'}
                  </p>
                  {updatedAt && (
                    <p className="text-[10px] text-slate-400 mt-1">Mis a jour le {new Date(updatedAt).toLocaleString()}</p>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {pastTensions.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-200 space-y-2">
            <p className="text-xs uppercase font-semibold text-slate-500">Historique</p>
            {pastTensions.map((tension) => (
              <div key={tension.id} className="w-full text-left p-3 rounded-lg border border-slate-200 bg-white">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-900 text-sm">
                    {tension.title || tension.original_data?.name || 'Tension sans titre'}
                  </div>
                  <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">
                    {tensionStatusLabel(tension.status)}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Cercle :{' '}
                  {tension.circle_id
                    ? circles.find((c) => c.id === tension.circle_id)?.name || '?'
                    : tension.original_data?.name || '?'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="col-span-7 bg-white border border-slate-100 rounded-lg p-4 shadow-inner overflow-y-auto">
        <TensionEditor
          tensionDraft={tensionDraft}
          circles={circles}
          user={user}
          tensionVotes={tensionVotes}
          tensionVotesLoading={tensionVotesLoading}
          tensionsLoading={tensionsLoading}
          isFieldChanged={isFieldChanged}
          onUpdateField={onUpdateField}
          onUpdateProposedField={onUpdateProposedField}
          onUpdateList={onUpdateList}
          onChangeAction={onChangeAction}
          onDelete={onDelete}
          onPropose={onPropose}
          onWithdraw={onWithdraw}
          onCancel={onCancel}
          onSave={onSave}
          defaultCircleId={selectedCircle?.id}
        />
      </div>
    </div>
  </div>
  );
};

export default TensionsPanel;
